/** @format */

import { countriesMap } from 'src/utils/dataMap';
import type { CmsEmbedProvider } from '../types';

const FORM_LOADER_SRC_PATTERN = /FormLoader\.bundle\.js/i;
const HTML_MARKERS_PATTERN = /FormLoader|data-form-api-url/i;
const READY_EVENT = 'd365mkt-afterformload';
const PHONE_INPUT_SELECTOR = 'input[name="telephone1"]';
const PHONE_CHARACTERS_PATTERN = /^[+]?[\d\s().\-/]+$/;
const PHONE_MIN_DIGITS = 3;
const PHONE_MAX_DIGITS = 15;
const PHONE_INVALID_MESSAGE = 'Ungültige Telefonnummer';

const GLOBAL_KEYS = ['MsCrmMkt', 'MsCrmFormLoader', 'd365mkt', 'd365mktConfigure'];

function isValidMarketingPhone(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return false;
  if (!PHONE_CHARACTERS_PATTERN.test(trimmed)) return false;

  const digitCount = (trimmed.match(/\d/g) || []).length;
  return digitCount >= PHONE_MIN_DIGITS && digitCount <= PHONE_MAX_DIGITS;
}

function ensurePhoneErrorElement(fieldBlock) {
  let errorEl = fieldBlock.querySelector('.phoneFormFieldBlock__error');
  if (errorEl) return errorEl;

  errorEl = document.createElement('span');
  errorEl.className = 'phoneFormFieldBlock__error';
  errorEl.setAttribute('role', 'alert');
  errorEl.setAttribute('aria-live', 'polite');
  fieldBlock.appendChild(errorEl);
  return errorEl;
}

function validateMarketingPhoneInput(input) {
  const fieldBlock = input.closest('.phoneFormFieldBlock');
  const value = input.value.trim();
  const errorEl = fieldBlock ? ensurePhoneErrorElement(fieldBlock) : null;

  if (!value) {
    input.setCustomValidity('');
    fieldBlock?.classList.remove('phoneFormFieldBlock--invalid');
    if (errorEl) errorEl.textContent = '';
    return;
  }

  if (isValidMarketingPhone(value)) {
    input.setCustomValidity('');
    fieldBlock?.classList.remove('phoneFormFieldBlock--invalid');
    if (errorEl) errorEl.textContent = '';
    return;
  }

  input.setCustomValidity(PHONE_INVALID_MESSAGE);
  fieldBlock?.classList.add('phoneFormFieldBlock--invalid');
  if (errorEl) errorEl.textContent = PHONE_INVALID_MESSAGE;
}

function bindMarketingPhoneValidation(form, signal) {
  const phoneInput = form.querySelector(PHONE_INPUT_SELECTOR);
  if (!phoneInput || phoneInput.dataset.phoneValidationBound === 'true') return;

  phoneInput.dataset.phoneValidationBound = 'true';

  const onPhoneChange = () => validateMarketingPhoneInput(phoneInput);
  const listenerOptions = signal ? { signal } : undefined;

  phoneInput.addEventListener('input', onPhoneChange, listenerOptions);
  phoneInput.addEventListener('change', onPhoneChange, listenerOptions);
  phoneInput.addEventListener('blur', onPhoneChange, listenerOptions);

  onPhoneChange();
}

export function setupMarketingFormPhoneValidation(root: Element | null | undefined, signal?: AbortSignal): void {
  if (!root) return;

  const forms = root.matches?.('form.marketingForm') ? [root] : [...root.querySelectorAll('form.marketingForm')];

  forms.forEach(form => bindMarketingPhoneValidation(form, signal));
}

function clearWindowGlobals(keys) {
  if (typeof window === 'undefined') return;

  keys.forEach(key => {
    const descriptor = Object.getOwnPropertyDescriptor(window, key);
    if (!descriptor) return;

    if (descriptor.configurable) {
      delete window[key];
      return;
    }

    if (descriptor.writable) {
      window[key] = undefined;
    }
  });
}

function prefillMarketingForm(form, userData) {
  const map = {
    firstname: userData.firstName,
    lastname: userData.lastName,
    emailaddress1: userData.email,
    telephone1: userData.address?.phoneNumber,
    ava_companyname: userData.company,
    address1_line1: userData.address?.street,
    address1_postalcode: userData.address?.postalCode,
    address1_city: userData.address?.city,
    address1_country: userData.address?.country ? countriesMap.get(userData.address.country) : ''
  };

  Object.entries(map).forEach(([name, value]) => {
    if (!value) return;
    const el = form.querySelector(`[name="${name}"]`);
    if (!el || el.type === 'checkbox') return;
    el.value = value;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

export const dynamics365MarketingProvider: CmsEmbedProvider = {
  id: 'dynamics365-marketing',
  readyEventName: READY_EVENT,

  matchHtml(html) {
    return HTML_MARKERS_PATTERN.test(html || '');
  },

  matchScript(src) {
    return FORM_LOADER_SRC_PATTERN.test(src || '');
  },

  shouldCacheBust(_src) {
    return true;
  },

  cleanup() {
    if (typeof document === 'undefined') return;

    document.querySelectorAll('script[src*="FormLoader"]').forEach(el => el.remove());
    document.querySelectorAll('iframe[src*="dynamics.com"], iframe[src*="mkt.dynamics"]').forEach(el => el.remove());

    clearWindowGlobals(GLOBAL_KEYS);
  },

  watchReady({ container, clearLoading }) {
    const onReady = (event: Event) => {
      if (!(event.target instanceof Node) || !container.contains(event.target)) return;
      clearLoading();
    };

    document.addEventListener(READY_EVENT, onReady);
    return () => document.removeEventListener(READY_EVENT, onReady);
  },

  onEmbedReady({ event, userData }) {
    const target = event.target as Element | null;
    const form =
      target?.querySelector?.('form.marketingForm') || target?.querySelector?.('form');
    if (!form) return;

    setupMarketingFormPhoneValidation(form);
    if (userData) prefillMarketingForm(form, userData);
  }
};
